import { entityTree, flattenEntityTree, compareTreeOrder, type TreeNode } from "./entityTree";
/** Pure helpers for filtering, grouping and ordering issues on the client. */
import type { Issue, ProjectData, WorkflowState } from "./store";
import { liveStates } from "./store";
import { isActiveStatus, isFinalStatus, matchesIssueTab, type IssueTab } from "./workflow";
import type { ViewSettings } from "./views";

export const PRIORITIES = [
  { value: 0, key: "none" },
  { value: 1, key: "urgent" },
  { value: 2, key: "high" },
  { value: 3, key: "medium" },
  { value: 4, key: "low" },
] as const;

export const STATE_TYPE_ORDER = ["backlog", "unstarted", "started", "pending", "done", "completed", "canceled"] as const;

export function stateOf(d: ProjectData, issue: Issue): WorkflowState | undefined {
  return d.states[issue.state_id];
}

export function isActive(d: ProjectData, issue: Issue) {
  const t = stateOf(d, issue)?.type;
  return isActiveStatus(t);
}

export function isBacklog(d: ProjectData, issue: Issue) {
  return stateOf(d, issue)?.type === "backlog";
}

export function isClosed(d: ProjectData, issue: Issue) {
  const t = stateOf(d, issue)?.type;
  return isFinalStatus(t);
}

const DAY = 86_400_000;

export function applyView(d: ProjectData, issues: Issue[], view: ViewSettings, tab: IssueTab): Issue[] {
  const now = Date.now();
  const f = view.filters;
  const text = f.text?.trim().toLowerCase();
  return issues.filter((i) => {
    if (!matchesIssueTab(stateOf(d, i)?.type, tab)) return false;
    if (!view.showSubIssues && i.parent_id) return false;
    if (isClosed(d, i) && tab === "all") {
      const closedAt = Date.parse(i.completed_at ?? i.canceled_at ?? i.updated_at);
      if (view.showCompleted === "none") return false;
      if (view.showCompleted === "week" && now - closedAt > 7 * DAY) return false;
      if (view.showCompleted === "month" && now - closedAt > 30 * DAY) return false;
    }
    if (f.states?.length && !f.states.includes(i.state_id)) return false;
    if (f.stateTypes?.length && !f.stateTypes.includes(stateOf(d, i)?.type ?? "")) return false;
    if (f.assignees?.length && !f.assignees.includes(i.assignee_id ?? null)) return false;
    if (f.priorities?.length && !f.priorities.includes(i.priority)) return false;
    if (f.labels?.length && !f.labels.some((l) => i.label_ids.includes(l))) return false;
    if (f.tasks?.length && !f.tasks.includes(i.task_id ?? null)) return false;
    if (text && !`${i.title} ${d.project.key}-${i.number}`.toLowerCase().includes(text)) return false;
    return true;
  });
}

export function sortIssues(issues: Issue[], orderBy: ViewSettings["orderBy"]): Issue[] {
  const arr = [...issues];
  switch (orderBy) {
    case "manual":
      return arr.sort(compareTreeOrder);
    case "priority":
      return arr.sort((a, b) => prio(a) - prio(b) || cmpDesc(a.updated_at, b.updated_at));
    case "updated":
      return arr.sort((a, b) => cmpDesc(a.updated_at, b.updated_at));
    case "created":
      return arr.sort((a, b) => cmpDesc(a.created_at, b.created_at));
    case "due":
      return arr.sort((a, b) => (a.due_date ?? "9999") < (b.due_date ?? "9999") ? -1 : 1);
  }
}

// Priority "none" (0) sorts last, like Linear.
const prio = (i: Issue) => (i.priority === 0 ? 5 : i.priority);
const cmpDesc = (a: string, b: string) => (a < b ? 1 : a > b ? -1 : 0);

export type Group = { key: string; label: string; color?: string; issues: Issue[]; state?: WorkflowState };

export function groupIssues(d: ProjectData, issues: Issue[], view: ViewSettings, t: (k: string) => string): Group[] {
  const sorted = sortIssues(issues, view.orderBy);
  const buckets = new Map<string, Group>();
  const add = (key: string, label: string, i: Issue | null, color?: string, state?: WorkflowState) => {
    if (!buckets.has(key)) buckets.set(key, { key, label, color, issues: [], state });
    if (i) buckets.get(key)!.issues.push(i);
  };
  switch (view.groupBy) {
    case "none":
      add("all", "", null);
      for (const i of sorted) add("all", "", i);
      break;
    case "state": {
      const states = liveStates(d).sort((a, b) => STATE_TYPE_ORDER.indexOf(a.type as never) - STATE_TYPE_ORDER.indexOf(b.type as never) || a.position - b.position);
      if (view.showEmptyGroups) for (const s of states) add(s.id, s.name, null, s.color, s);
      for (const i of sorted) {
        const s = d.states[i.state_id];
        add(i.state_id, s?.name ?? "?", i, s?.color, s);
      }
      // order buckets like the states list
      return states.map((s) => buckets.get(s.id)).filter((g): g is Group => !!g && (view.showEmptyGroups || g.issues.length > 0));
    }
    case "priority":
      if (view.showEmptyGroups) for (const p of PRIORITIES) add(String(p.value), t(`priority.${p.key}`), null);
      for (const i of sorted) add(String(i.priority), t(`priority.${PRIORITIES[i.priority]?.key ?? "none"}`), i);
      return [1, 2, 3, 4, 0].map((p) => buckets.get(String(p))).filter((g): g is Group => !!g);
    case "assignee":
      for (const i of sorted) {
        const u = i.assignee_id ? d.users[i.assignee_id] : undefined;
        add(i.assignee_id ?? "none", u?.name ?? t("issue.unassigned"), i);
      }
      break;
    case "label":
      for (const i of sorted) {
        if (i.label_ids.length === 0) add("none", t("issue.noLabel"), i);
        for (const l of i.label_ids) add(l, d.labels[l]?.name ?? "?", i, d.labels[l]?.color);
      }
      break;
    case "task":
      for (const i of sorted) {
        const tk = i.task_id ? d.tasks[i.task_id] : undefined;
        add(i.task_id ?? "none", tk?.name ?? t("issue.noTask"), i, tk?.color ?? undefined);
      }
      break;
  }
  return [...buckets.values()].sort((a, b) => (a.key === "none" ? 1 : b.key === "none" ? -1 : a.label.localeCompare(b.label)));
}

export type IssueTreeGroup = Group & { rows: TreeNode<Issue>[] };

/** Group whole visible subtrees by their root; each child appears beneath its parent. */
export function groupIssueTrees(d: ProjectData, issues: Issue[], view: ViewSettings, t: (key: string) => string, collapsed: Set<string>): IssueTreeGroup[] {
  const ranks = new Map(sortIssues(issues, view.orderBy).map((issue, index) => [issue.id, index]));
  const roots = entityTree(issues, (a, b) => ranks.get(a.id)! - ranks.get(b.id)!);
  const byId = new Map(roots.map((node) => [node.item.id, node]));
  return groupIssues(d, roots.map((node) => node.item), view, t).map((group) => {
    const nodes = group.issues.map((issue) => byId.get(issue.id)!);
    return { ...group, issues: flattenEntityTree(nodes, new Set()).map((node) => node.item), rows: flattenEntityTree(nodes, collapsed) };
  });
}

export function formatRelative(iso: string, locale: string): string {
  const diff = Date.now() - Date.parse(iso);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const m = Math.round(diff / 60_000);
  if (m < 1) return rtf.format(0, "second");
  if (m < 60) return rtf.format(-m, "minute");
  const h = Math.round(m / 60);
  if (h < 24) return rtf.format(-h, "hour");
  const dd = Math.round(h / 24);
  if (dd < 30) return rtf.format(-dd, "day");
  return new Date(iso).toLocaleDateString(locale, { month: "short", day: "numeric" });
}

export function formatDue(date: string, locale: string): { text: string; tone: "normal" | "soon" | "late" } {
  const d = new Date(date + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - today.getTime()) / DAY);
  const text = d.toLocaleDateString(locale, { month: "short", day: "numeric" });
  return { text, tone: days < 0 ? "late" : days <= 2 ? "soon" : "normal" };
}
